import { Box, Flex, Icon, Link, Spinner, Stack, Text } from '@chakra-ui/react';
import { FiFile } from 'react-icons/fi';
import { configApi } from '../../../services/configApi';
import { useRegistrationDocumentStatus } from '../../../services/hooks/Registration/useVacancyDocuments';
import { formatFileSize } from '../../../utils/functions';

interface IOwnProps {
  registrationId: string;
  isMobile?: boolean;
}

const getAttachmentUrl = (attachment: { id: string; name: string }) => {
  const extension = attachment.name.split('.').pop()?.toLowerCase();
  if (!extension || !configApi.defaults.baseURL) return undefined;

  return new URL(`media/attachment/registration/${attachment.id}.${extension}`, configApi.defaults.baseURL).toString();
};

const RegistrationDocumentsSummary: React.FC<IOwnProps> = ({ registrationId, isMobile = false }) => {
  const statusQuery = useRegistrationDocumentStatus(registrationId);
  const uploadedDocuments =
    statusQuery.data?.documents.filter((document) => document.uploaded && document.attachment) ?? [];

  return (
    <Stack mt={4} spacing={3} w="100%" minW={0} overflow="hidden">
      <Text fontSize={isMobile ? 'sm' : 'md'} fontWeight="bold" textTransform="uppercase">
        Documentos enviados
      </Text>

      {statusQuery.isLoading && (
        <Flex align="center" gap={2} color="gray.500">
          <Spinner size="sm" />
          <Text fontSize="sm">Buscando documentos...</Text>
        </Flex>
      )}

      {statusQuery.isError && (
        <Text fontSize="sm" color="red.500">
          Não foi possível carregar os documentos.
        </Text>
      )}

      {!statusQuery.isLoading && !statusQuery.isError && uploadedDocuments.length === 0 && (
        <Text fontSize="sm" color="gray.500">
          Nenhum documento enviado.
        </Text>
      )}

      <Stack spacing={2}>
        {uploadedDocuments.map((document) => {
          const attachment = document.attachment;
          if (!attachment) return null;

          return (
            <Box
              key={document.type}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              bg="white"
              p={3}
              boxShadow={isMobile ? 'sm' : undefined}
              w="100%"
              minW={0}
              overflow="hidden"
            >
              <Flex align="center" gap={3} minW={0}>
                <Icon as={FiFile} boxSize={5} color="blue.500" flexShrink={0} />
                <Stack spacing={0} minW={0} flex={1} overflow="hidden">
                  <Text fontSize="sm" fontWeight="semibold">
                    {document.label}
                  </Text>
                  <Link
                    href={getAttachmentUrl(attachment)}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="blue.600"
                    fontSize="sm"
                    textDecoration="underline"
                    isTruncated
                    display="block"
                    w="100%"
                    maxW="100%"
                    title={attachment.name}
                  >
                    {attachment.name}
                  </Link>
                  <Text fontSize="xs" color="gray.500">
                    {formatFileSize(attachment.size)} · {new Date(attachment.created_at).toLocaleString('pt-BR')}
                  </Text>
                </Stack>
              </Flex>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default RegistrationDocumentsSummary;
